import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { PreferencesFormMeta, PreferencesFormValues } from 'components/notificationPreferences/NotificationPreferences';
import Checkbox from 'components/shared/form/Checkbox';
import Toggle from 'components/shared/Toggle';
import { TabType } from 'components/Tabs';
import { Flags } from 'constants/flags';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import { capitalize, humanize } from 'lib/util';
import { NotificationChannel, NotificationChannels } from 'models/Notifications';
import colors from 'styles/theme/colors';
import { BodySmall, BodySmallBold, Label } from 'styles/typography';
import ChevronDownBold from 'svg/ChevronDownBold';
import ChevronRightBold from 'svg/ChevronRightBold';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  criticalAlerts: 'Time-sensitive events, escalations, authorization deadlines, and safe return activity.',
  activity: "Updates to a patient's record: new documents, added information, and stage changes.",
  referrals: "Updates on referrals you've sent out, from creation through acceptance or denial.",
  patientStatusChanges: "Changes to a patient's progress status",
};

// Display titles that differ from the humanized stored key (the stored
// `referrals` category name is deliberately kept).
const CATEGORY_TITLES: Record<string, string> = {
  referrals: 'Outbound Referrals',
};

type Props = {
  category: string;
  disabled?: boolean;
  selectedTab: TabType;
};

export default function NotificationCategoryPreferences({ selectedTab, category, disabled = false }: Props) {
  const { values, setValue } = useForm<PreferencesFormValues, PreferencesFormMeta>();
  const { translateAll } = useTranslation();
  const { profile } = useProfile();
  const [expanded, setExpanded] = useState(false);

  const showInApp = profile.hasFlag(Flags.InAppNotifications);

  const categoryValues = useMemo(
    () =>
      (values[selectedTab.value]?.notificationSelections[category] ?? {}) as Record<string, NotificationChannels> & {
        active?: boolean;
      },
    [values, selectedTab.value, category]
  );

  // `active` is the category's stored master switch, a sibling of the kind keys.
  const categoryActive = categoryValues.active !== false;
  const categoryPreferences = useMemo(
    () =>
      Object.fromEntries(Object.entries(categoryValues).filter(([key]) => key !== 'active')) as Record<
        string,
        NotificationChannels
      >,
    [categoryValues]
  );

  const _setPref = (key: string, channel: NotificationChannel, val: boolean) => {
    setValue(`${selectedTab.value}.notificationSelections.${category}.${key}.${channel}`, val);
  };
  const handleSelectAll = (val: boolean, channel: NotificationChannel) => {
    const notificationTypes = Object.keys(categoryPreferences);

    notificationTypes.forEach((type) => {
      const channelValue = categoryPreferences[type]?.[channel];

      if (channelValue === undefined) return;
      _setPref(type, channel, val);
    });
  };

  // The master switch mutes the category's delivery without touching the
  // checkbox selections, which stay stored for when it comes back on.
  const handleCategoryToggle = (val: boolean) => {
    setValue(`${selectedTab.value}.notificationSelections.${category}.active`, val);
  };

  // A role template can omit a category entirely; [].every() is true, which
  // would render the select-alls checked over zero rows.
  const hasKinds = Object.keys(categoryPreferences).length > 0;

  const allEmailSelected = useMemo(() => {
    return hasKinds && Object.values(categoryPreferences).every((v) => v.email);
  }, [hasKinds, categoryPreferences]);

  const allInAppSelected = useMemo(() => {
    return hasKinds && Object.values(categoryPreferences).every((v) => v.inApp);
  }, [hasKinds, categoryPreferences]);

  return (
    <Card data-testid={`category-card-${category}`}>
      <CardHeader
        data-cy='clickable'
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((val) => !val)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((val) => !val);
          }
        }}>
        <Chevron>{expanded ? <ChevronDownBold /> : <ChevronRightBold />}</Chevron>
        <HeaderText>
          <CategoryTitle>{CATEGORY_TITLES[category] ?? capitalize(humanize(category))}</CategoryTitle>
          <CategoryDescription>{CATEGORY_DESCRIPTIONS[category]}</CategoryDescription>
        </HeaderText>
        <ToggleContainer onClick={(event) => event.stopPropagation()}>
          <ToggleLabel>{categoryActive ? 'On' : 'Off'}</ToggleLabel>
          <Toggle active={categoryActive} disabled={disabled} onChange={handleCategoryToggle} />
        </ToggleContainer>
      </CardHeader>

      {expanded && (
        <List $muted={!categoryActive}>
          <ListItem>
            <Cell></Cell>
            {showInApp && (
              <CheckboxContainer>
                <HeaderLabel>In-App</HeaderLabel>
                <Checkbox
                  key={`${selectedTab.value}-all-in-app`}
                  dataCyLabel='select-all-checkbox-in-app'
                  disabled={disabled || !categoryActive || !hasKinds}
                  size={14}
                  color={colors.black100}
                  checkedColor={colors.blue100}
                  labelMargin='0px'
                  checked={allInAppSelected}
                  onChange={(val) => handleSelectAll(val, NotificationChannel.IN_APP)}
                />
              </CheckboxContainer>
            )}
            <CheckboxContainer>
              <HeaderLabel>Email</HeaderLabel>
              <Checkbox
                key={`${selectedTab.value}-all-email`}
                dataCyLabel='select-all-checkbox-email'
                disabled={disabled || !categoryActive || !hasKinds}
                size={14}
                color={colors.black100}
                checkedColor={colors.blue100}
                labelMargin='0px'
                checked={allEmailSelected}
                onChange={(val) => handleSelectAll(val, NotificationChannel.EMAIL)}
              />
            </CheckboxContainer>
          </ListItem>

          {Object.entries(categoryPreferences)
            .sort()
            .map(([key, _val]: [string, any]) => {
              return (
                <ListItem key={key}>
                  <Cell>
                    <NotificationLabel>{translateAll(capitalize(humanize(key)))}</NotificationLabel>
                  </Cell>
                  {showInApp && (
                    <CheckboxContainer>
                      <Checkbox
                        disabled={disabled || !categoryActive}
                        size={14}
                        color={colors.black100}
                        checkedColor={colors.blue100}
                        labelMargin='0px'
                        checked={categoryPreferences[key]?.inApp}
                        onChange={(val) => _setPref(key, NotificationChannel.IN_APP, val)}
                      />
                    </CheckboxContainer>
                  )}
                  <CheckboxContainer>
                    <Checkbox
                      disabled={disabled || !categoryActive}
                      size={14}
                      color={colors.black100}
                      checkedColor={colors.blue100}
                      labelMargin='0px'
                      checked={categoryPreferences[key]?.email}
                      onChange={(val) => _setPref(key, NotificationChannel.EMAIL, val)}
                    />
                  </CheckboxContainer>
                </ListItem>
              );
            })}
        </List>
      )}
    </Card>
  );
}

const Card = styled.div`
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: 16px;
  padding: 16px 24px;
`;

const CardHeader = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 12px;
`;

const Chevron = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.black100};
  display: flex;
  flex-shrink: 0;
  width: 14px;
`;

const HeaderText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const CategoryTitle = styled(BodySmallBold)`
  font-size: 14px;
`;

const CategoryDescription = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
  font-size: 12px;
`;

const ToggleContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const ToggleLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
`;

const HeaderLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
  margin-bottom: 10px;
`;

const NotificationLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
  font-weight: 500;
`;

const List = styled.div<{ $muted?: boolean }>`
  margin-top: 16px;
  opacity: ${({ $muted }) => ($muted ? 0.5 : 1)};
`;

const ListItem = styled.div`
  display: flex;
  padding: 16px 0px;
  border-bottom: 1px solid ${(props) => props.theme.colors.black10};
  align-items: center;
  line-height: 16px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Cell = styled.div<{ style?: any }>`
  min-width: 150px;
  flex: 1;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 48px;
  align-items: center;
  text-align: center;

  & + & {
    margin-left: 8px;
  }
`;
